import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  ExternalLink,
  Package,
  MapPin,
  Calendar,
  Phone,
  Mail,
  Globe,
  Users,
  Clock,
  Tag,
  UserPlus,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { Opportunity, BuyerOrganizationRole, OpportunityLot } from '../../types/internal-opportunity.types';
import { COMPANY_TYPE_CONFIG } from '../../types/internal-opportunity.types';
import { CreateBuyerForOpportunityDialog } from '../companies/CreateBuyerForOpportunityDialog';

// Mapping des départements français
const DEPARTMENTS: Record<string, string> = {
  '01': 'Ain',
  '02': 'Aisne',
  '03': 'Allier',
  '04': 'Alpes-de-Haute-Provence',
  '05': 'Hautes-Alpes',
  '06': 'Alpes-Maritimes',
  '07': 'Ardèche',
  '08': 'Ardennes',
  '09': 'Ariège',
  '10': 'Aube',
  '11': 'Aude',
  '12': 'Aveyron',
  '13': 'Bouches-du-Rhône',
  '14': 'Calvados',
  '15': 'Cantal',
  '16': 'Charente',
  '17': 'Charente-Maritime',
  '18': 'Cher',
  '19': 'Corrèze',
  '2A': 'Corse-du-Sud',
  '2B': 'Haute-Corse',
  '21': 'Côte-d\'Or',
  '22': 'Côtes-d\'Armor',
  '23': 'Creuse',
  '24': 'Dordogne',
  '25': 'Doubs',
  '26': 'Drôme',
  '27': 'Eure',
  '28': 'Eure-et-Loir',
  '29': 'Finistère',
  '30': 'Gard',
  '31': 'Haute-Garonne',
  '32': 'Gers',
  '33': 'Gironde',
  '34': 'Hérault',
  '35': 'Ille-et-Vilaine',
  '36': 'Indre',
  '37': 'Indre-et-Loire',
  '38': 'Isère',
  '39': 'Jura',
  '40': 'Landes',
  '41': 'Loir-et-Cher',
  '42': 'Loire',
  '43': 'Haute-Loire',
  '44': 'Loire-Atlantique',
  '45': 'Loiret',
  '46': 'Lot',
  '47': 'Lot-et-Garonne',
  '48': 'Lozère',
  '49': 'Maine-et-Loire',
  '50': 'Manche',
  '51': 'Marne',
  '52': 'Haute-Marne',
  '53': 'Mayenne',
  '54': 'Meurthe-et-Moselle',
  '55': 'Meuse',
  '56': 'Morbihan',
  '57': 'Moselle',
  '58': 'Nièvre',
  '59': 'Nord',
  '60': 'Oise',
  '61': 'Orne',
  '62': 'Pas-de-Calais',
  '63': 'Puy-de-Dôme',
  '64': 'Pyrénées-Atlantiques',
  '65': 'Hautes-Pyrénées',
  '66': 'Pyrénées-Orientales',
  '67': 'Bas-Rhin',
  '68': 'Haut-Rhin',
  '69': 'Rhône',
  '70': 'Haute-Saône',
  '71': 'Saône-et-Loire',
  '72': 'Sarthe',
  '73': 'Savoie',
  '74': 'Haute-Savoie',
  '75': 'Paris',
  '76': 'Seine-Maritime',
  '77': 'Seine-et-Marne',
  '78': 'Yvelines',
  '79': 'Deux-Sèvres',
  '80': 'Somme',
  '81': 'Tarn',
  '82': 'Tarn-et-Garonne',
  '83': 'Var',
  '84': 'Vaucluse',
  '85': 'Vendée',
  '86': 'Vienne',
  '87': 'Haute-Vienne',
  '88': 'Vosges',
  '89': 'Yonne',
  '90': 'Territoire de Belfort',
  '91': 'Essonne',
  '92': 'Hauts-de-Seine',
  '93': 'Seine-Saint-Denis',
  '94': 'Val-de-Marne',
  '95': 'Val-d\'Oise',
  '971': 'Guadeloupe',
  '972': 'Martinique',
  '973': 'Guyane',
  '974': 'La Réunion',
  '976': 'Mayotte',
};

// Mapping des rôles d'organisation
const ORGANIZATION_ROLES: Record<BuyerOrganizationRole, string> = {
  buyer: 'Acheteur',
  additional_info: 'Informations complémentaires',
  document_provider: 'Accès aux documents',
  tender_evaluator: 'Traitement des offres',
  appeal_receiver: 'Procédures de recours',
  appeal_info: 'Précisions sur les recours',
  mediator: 'Médiation',
};

interface OpportunityInfoProps {
  opportunity: Opportunity;
}

export function OpportunityInfo({ opportunity }: OpportunityInfoProps) {
  const [selectedLot, setSelectedLot] = useState<OpportunityLot | null>(null);
  const [createBuyerOpen, setCreateBuyerOpen] = useState(false);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatAmount = (amount?: number) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: opportunity.currency || 'EUR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getMarketTypeLabel = (type?: string) => {
    switch (type) {
      case 'services':
        return 'Services';
      case 'supplies':
        return 'Fournitures';
      case 'works':
        return 'Travaux';
      default:
        return '-';
    }
  };

  const getSourceLabel = (source?: string) => {
    switch (source) {
      case 'boamp':
        return 'BOAMP';
      case 'ted':
        return 'TED (Europe)';
      case 'manual':
        return 'Saisie manuelle';
      default:
        return source || '-';
    }
  };

  const getDepartmentLabel = (code?: string) => {
    if (!code) return null;
    const name = DEPARTMENTS[code];
    return name ? `${name} (${code})` : code;
  };

  // Nettoie le texte en supprimant les sauts de ligne multiples et les espaces inutiles
  const cleanText = (text?: string) => {
    if (!text) return '';
    return text
      .replace(/\r\n/g, '\n') // Normalise les fins de ligne
      .replace(/\n{3,}/g, '\n\n') // Remplace 3+ sauts de ligne par 2
      .replace(/^\s+|\s+$/g, '') // Trim début/fin
      .replace(/[ \t]+/g, ' '); // Remplace multiples espaces par un seul
  };

  const buyerOrgName = opportunity.buyerOrganizations?.find(
    (org) => org.roles?.includes('buyer'),
  )?.name;
  const fallbackBuyerName =
    opportunity.buyerName === 'Acheteur non spécifié' ? undefined : opportunity.buyerName;
  const buyerDisplayName =
    fallbackBuyerName || opportunity.buyer?.name || buyerOrgName;

  // Calcul du délai restant
  const getDaysRemaining = () => {
    if (!opportunity.submissionDeadline) return null;
    const deadline = new Date(opportunity.submissionDeadline);
    const now = new Date();
    const diffTime = deadline.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysRemaining = getDaysRemaining();
  const isUrgent = daysRemaining !== null && daysRemaining <= 7 && daysRemaining > 0;
  const isExpired = daysRemaining !== null && daysRemaining <= 0;

  return (
    <div className="space-y-6">
      {/* Layout 2 colonnes pour les infos principales */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Colonne gauche */}
        <div className="space-y-6">
          {/* Dates importantes - Carte mise en avant */}
          <Card className={isExpired ? 'border-red-300 bg-red-50' : isUrgent ? 'border-amber-300 bg-amber-50' : ''}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Dates clés
                {isExpired && (
                  <Badge variant="destructive" className="ml-auto text-xs">
                    Expiré
                  </Badge>
                )}
                {isUrgent && !isExpired && (
                  <Badge className="ml-auto bg-amber-500 text-xs">
                    {daysRemaining}j restant{daysRemaining > 1 ? 's' : ''}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground text-xs">Publication</p>
                  <p className="font-medium">{formatDate(opportunity.publicationDate)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Limite de réponse</p>
                  <p className={`font-medium ${isExpired ? 'text-red-600' : isUrgent ? 'text-amber-600' : ''}`}>
                    {formatDateTime(opportunity.submissionDeadline)}
                  </p>
                  {daysRemaining !== null && (
                    <p className={`text-xs mt-0.5 ${
                      isExpired
                        ? 'text-red-600 font-semibold'
                        : isUrgent
                          ? 'text-amber-600 font-semibold'
                          : 'text-muted-foreground'
                    }`}>
                      {isExpired
                        ? daysRemaining === 0
                          ? "Expire aujourd'hui"
                          : `Dépassée depuis ${Math.abs(daysRemaining)} jour${Math.abs(daysRemaining) > 1 ? 's' : ''}`
                        : `${daysRemaining} jour${daysRemaining > 1 ? 's' : ''} restant${daysRemaining > 1 ? 's' : ''}`
                      }
                    </p>
                  )}
                </div>
                {opportunity.questionsDeadline && (
                  <div className="col-span-2">
                    <p className="text-muted-foreground text-xs">Limite questions</p>
                    <p className="font-medium">{formatDateTime(opportunity.questionsDeadline)}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Caractéristiques du marché */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Caractéristiques</CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground text-xs">Type</p>
                  <p className="font-medium">{getMarketTypeLabel(opportunity.marketType)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Procédure</p>
                  <p className="font-medium">{opportunity.procedureType || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Allotissement</p>
                  <p className="font-medium">
                    {opportunity.isMultiLot ? `Oui (${opportunity.lotsCount} lots)` : 'Non'}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Durée</p>
                  <p className="font-medium">
                    {opportunity.durationMonths ? `${opportunity.durationMonths} mois` : '-'}
                  </p>
                </div>
              </div>

              <Separator />

              {/* Montants */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="col-span-2">
                  <p className="text-muted-foreground text-xs">Montant estimé</p>
                  <p className="font-semibold text-lg">{formatAmount(opportunity.estimatedAmount)}</p>
                </div>
                {(opportunity.amountMin || opportunity.amountMax) && (
                  <>
                    {opportunity.amountMin && (
                      <div>
                        <p className="text-muted-foreground text-xs">Minimum</p>
                        <p className="font-medium">{formatAmount(opportunity.amountMin)}</p>
                      </div>
                    )}
                    {opportunity.amountMax && (
                      <div>
                        <p className="text-muted-foreground text-xs">Maximum</p>
                        <p className="font-medium">{formatAmount(opportunity.amountMax)}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Colonne droite */}
        <div className="space-y-6">
          {/* Acheteur */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Acheteur
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {opportunity.buyer ? (
                      <Link
                        to={`/tm/companies/${opportunity.buyer.id}`}
                        className="font-medium hover:text-copper hover:underline"
                      >
                        {opportunity.buyer.name}
                      </Link>
                    ) : (
                      <p className="font-medium">{buyerDisplayName || 'Non spécifié'}</p>
                    )}
                    {opportunity.buyer?.companyType && COMPANY_TYPE_CONFIG[opportunity.buyer.companyType] && (
                      <Badge
                        className={`text-xs ${COMPANY_TYPE_CONFIG[opportunity.buyer.companyType].bgColor} ${COMPANY_TYPE_CONFIG[opportunity.buyer.companyType].color}`}
                      >
                        {COMPANY_TYPE_CONFIG[opportunity.buyer.companyType].label}
                      </Badge>
                    )}
                    {!opportunity.buyer && (
                      <Badge variant="outline" className="text-xs text-amber-700 border-amber-300">
                        Fiche non liée
                      </Badge>
                    )}
                  </div>
                  {(opportunity.department || opportunity.region) && (
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {getDepartmentLabel(opportunity.department) || opportunity.region}
                    </p>
                  )}
                </div>
                {opportunity.importSource && (
                  <Badge variant="outline" className="text-xs">
                    {getSourceLabel(opportunity.importSource)}
                  </Badge>
                )}
              </div>

              {!opportunity.buyer && (
                <div className="pt-2 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCreateBuyerOpen(true)}
                    className="w-full gap-2"
                  >
                    <UserPlus className="h-4 w-4" />
                    Créer la fiche prospect
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2">
                    L'annonce ne fournit pas de SIRET : saisissez-le manuellement pour enrichir via INSEE.
                  </p>
                </div>
              )}

              <div className="flex flex-wrap gap-3 text-sm">
                {opportunity.sourceUrl && (
                  <a
                    href={opportunity.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-copper hover:underline"
                  >
                    <ExternalLink className="h-3 w-3" />
                    Avis original
                  </a>
                )}
                {opportunity.externalId && (
                  <span className="text-muted-foreground">
                    Réf. {opportunity.externalId}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Description */}
          {opportunity.description && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Description</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {cleanText(opportunity.description)}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Décision - si présente */}
          {opportunity.decision && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Décision</CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-2">
                <div className="flex items-center gap-2">
                  <Badge
                    className={
                      opportunity.decision === 'go'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-red-100 text-red-700'
                    }
                  >
                    {opportunity.decision === 'go' ? 'GO' : 'NO-GO'}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    le {formatDate(opportunity.decisionDate)}
                  </span>
                </div>
                {opportunity.decisionReason && (
                  <p className="text-sm text-muted-foreground">
                    {opportunity.decisionReason}
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Lots */}
      {opportunity.lots && opportunity.lots.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Package className="h-4 w-4" />
              Lots ({opportunity.lots.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {opportunity.lots.map((lot) => (
                <div
                  key={lot.id}
                  className="border rounded-lg p-3 bg-muted/30 cursor-pointer hover:bg-muted/50 hover:border-copper/50 transition-colors"
                  onClick={() => setSelectedLot(lot)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && setSelectedLot(lot)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">
                        Lot {lot.lotNumber}: {lot.title}
                      </p>
                      {lot.description && (
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                          {cleanText(lot.description)}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      {lot.estimatedAmount && lot.estimatedAmount > 0 ? (
                        <Badge variant="outline" className="text-xs">
                          {formatAmount(lot.estimatedAmount)}
                        </Badge>
                      ) : lot.amountMax ? (
                        <Badge variant="outline" className="text-xs">
                          Max: {formatAmount(lot.amountMax)}
                        </Badge>
                      ) : null}
                      {lot.amountMax && lot.estimatedAmount && lot.estimatedAmount > 0 && lot.amountMax !== lot.estimatedAmount && (
                        <span className="text-xs text-muted-foreground">
                          Max: {formatAmount(lot.amountMax)}
                        </span>
                      )}
                    </div>
                  </div>
                  {(lot.durationMonths || lot.cpvCode) && (
                    <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mt-2">
                      {lot.durationMonths && (
                        <span>Durée: {lot.durationMonths} mois</span>
                      )}
                      {lot.cpvCode && (
                        <span>CPV: {lot.cpvCode}</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dialog détail du lot */}
      <Dialog open={!!selectedLot} onOpenChange={(open) => !open && setSelectedLot(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-copper" />
              Lot {selectedLot?.lotNumber}
            </DialogTitle>
          </DialogHeader>
          {selectedLot && (
            <div className="space-y-4">
              {/* Titre */}
              <div>
                <h3 className="font-semibold text-base">{selectedLot.title}</h3>
              </div>

              {/* Description complète */}
              {selectedLot.description && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                    Description
                  </p>
                  <p className="text-sm whitespace-pre-wrap">
                    {cleanText(selectedLot.description)}
                  </p>
                </div>
              )}

              <Separator />

              {/* Informations financières */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                    Montant estimé
                  </p>
                  <p className="font-semibold text-lg">
                    {selectedLot.estimatedAmount && selectedLot.estimatedAmount > 0
                      ? formatAmount(selectedLot.estimatedAmount)
                      : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                    Montant maximum
                  </p>
                  <p className="font-semibold text-lg">
                    {selectedLot.amountMax ? formatAmount(selectedLot.amountMax) : '-'}
                  </p>
                </div>
              </div>

              {/* Durée et CPV */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-start gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                      Durée
                    </p>
                    <p className="text-sm font-medium">
                      {selectedLot.durationMonths
                        ? `${selectedLot.durationMonths} mois`
                        : '-'}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Tag className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                      Code CPV
                    </p>
                    <p className="text-sm font-medium font-mono">
                      {selectedLot.cpvCode || '-'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Organisations - pleine largeur comme les lots */}
      {opportunity.buyerOrganizations && opportunity.buyerOrganizations.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4" />
              Organisations ({opportunity.buyerOrganizations.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {opportunity.buyerOrganizations.map((org) => (
                <div
                  key={org.id}
                  className="border rounded-lg p-3 bg-muted/30 space-y-2"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{org.name}</p>
                      {org.registrationNumber && (
                        <p className="text-xs text-muted-foreground">
                          N° {org.registrationNumber}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1 justify-end shrink-0">
                      {org.roles.slice(0, 2).map((role) => (
                        <Badge key={role} variant="outline" className="text-xs">
                          {ORGANIZATION_ROLES[role] || role}
                        </Badge>
                      ))}
                      {org.roles.length > 2 && (
                        <Badge variant="outline" className="text-xs">
                          +{org.roles.length - 2}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Adresse compact */}
                  {org.address && (org.address.city || org.address.postalCode) && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {org.address.postalCode} {org.address.city}
                    </p>
                  )}

                  {/* Contact compact */}
                  {org.contact && (org.contact.email || org.contact.phone) && (
                    <div className="flex flex-wrap gap-3 text-xs">
                      {org.contact.phone && (
                        <a href={`tel:${org.contact.phone}`} className="flex items-center gap-1 text-copper hover:underline">
                          <Phone className="h-3 w-3" />
                          {org.contact.phone}
                        </a>
                      )}
                      {org.contact.email && (
                        <a href={`mailto:${org.contact.email}`} className="flex items-center gap-1 text-copper hover:underline">
                          <Mail className="h-3 w-3" />
                          {org.contact.email}
                        </a>
                      )}
                    </div>
                  )}

                  {org.buyerProfileUrl && (
                    <a
                      href={org.buyerProfileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-copper hover:underline"
                    >
                      <Globe className="h-3 w-3" />
                      Profil acheteur
                    </a>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Critères d'attribution */}
      {opportunity.awardCriteria && opportunity.awardCriteria.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Critères d'attribution</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {opportunity.awardCriteria.map((criterion, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 bg-muted/50 rounded text-sm"
                >
                  <span className="truncate mr-2">{criterion.name}</span>
                  <Badge variant="secondary" className="shrink-0">{criterion.weight}%</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tags et Notes en ligne si présents */}
      {((opportunity.tags && opportunity.tags.length > 0) || opportunity.notes) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {opportunity.tags && opportunity.tags.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Tags</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex flex-wrap gap-1.5">
                  {opportunity.tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {opportunity.notes && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Notes internes</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {cleanText(opportunity.notes)}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <CreateBuyerForOpportunityDialog
        open={createBuyerOpen}
        onOpenChange={setCreateBuyerOpen}
        opportunityId={opportunity.id}
        suggestedName={buyerDisplayName || undefined}
      />
    </div>
  );
}
